import React, { lazy, Suspense } from "react";
import { NextUIProvider } from "@nextui-org/react";
import { Toaster } from "react-hot-toast";
import "./index.css";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import LoginPage from "./screens/LogIn/LogIn";

import ScrollToTop from "./ScrollToTop"; // ScrollToTop component
import { Provider } from "react-redux";
import { store, persistor } from "./Redux/Store";
import { PersistGate } from "redux-persist/integration/react";
import { PrivateRoute, PublicRoute } from "./utils/ProtectedRoute";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";

import AnalyticsTracker from "./components/AnalyticsTracker";
import GlobalLiveAgentNotifier from "./screens/ChatbotAnalytics/live/GlobalLiveAgentNotifier";

import { initGA } from "./Analytics/Analytics";

// Load screens on navigation while login and global services remain immediate.
const DrildownAppcopilot = lazy(() =>
  import("./screens/DrildownAppcopilotV2").then((module) => ({
    default: module.DrildownAppcopilot,
  }))
);
const PropertyUnitDetails = lazy(
  () => import("./screens/PropertyUnitDetails/Index")
);
const PropertyunitsV2 = lazy(
  () => import("./screens/PropertyUnits/PropertyunitsV2")
);
const ForgotPassword = lazy(() =>
  import("./screens/Forgotpassword").then((module) => ({
    default: module.ForgotPassword,
  }))
);
const ChangePasswordPage = lazy(() =>
  import("./screens/ChangePsw").then((module) => ({
    default: module.ChangePasswordPage,
  }))
);
const ResetPassword = lazy(() =>
  import("./screens/ResetPassword").then((module) => ({
    default: module.ResetPassword,
  }))
);
const Profile = lazy(() => import("./screens/Profile/Profile"));
const Settings = lazy(() => import("./screens/Settings/Settings"));
const Support = lazy(() => import("./screens/Support/Support"));
const Invite = lazy(() => import("./screens/Invite/Invite"));
const InviteVerify = lazy(() => import("./screens/Invite/InviteVerify"));
const OnBoard = lazy(() => import("./screens/Invite/OnBoard"));
const TokenVerify = lazy(() => import("./components/TokenVerify"));
const PortfolioLogs = lazy(
  () => import("./screens/PortfolioLogs/PortfolioLogs")
);
const PropertyReport = lazy(
  () => import("./screens/DrildownAppcopilot/PropertyReport")
);
const DrilldownPropertyView = lazy(
  () => import("./screens/PropertyView/DrilldownPropertyView")
);
const UnitsSearch = lazy(() => import("./screens/UnitsSearch/UnitsSearch"));
const PortfolioDashboard = lazy(
  () => import("./screens/Dashboard/PortfolioDashboard")
);
const FileUpload = lazy(() => import("./screens/ImportProperties/FileUpload"));
const VacancyAnalysis = lazy(
  () => import("./screens/Analysis/VacancyAnalysis")
);
const LeaseUpAnalysis = lazy(
  () => import("./screens/Analysis/LeaseUpAnalysis")
);
const DelinquencyAnalysis = lazy(
  () => import("./screens/Analysis/DelinquncyAnalysis/DelinquencyAnalysis")
);
const Index = lazy(() => import("./components/ManageUnits/Index"));
const AnalyticsOverview = lazy(
  () => import("./components/BubbleChart/AnalyticsOverview")
);
const ScoreCardMOM = lazy(
  () => import("./components/BubbleChart/ScoreCardMOM")
);
const RulesForm = lazy(() => import("./components/RulesForm"));
const ExpenseRatio = lazy(() => import("./screens/ExpenseRatio/ExpenseRatio"));
const ProfitAndLoss = lazy(
  () => import("./screens/Corporate Accounting/Profit and Loss/Index")
);
const BalanceSheet = lazy(
  () => import("./screens/Corporate Accounting/BalanceSheet/BalanceSheet")
);
const Cashflow = lazy(
  () => import("./screens/Corporate Accounting/CashFlow/Cashflow")
);
const IndependentMarketResearchPage = lazy(
  () =>
    import("./screens/IndependentMarketResearch/IndependentMarketResearchPage")
);
const OwnerReview = lazy(() => import("./screens/OwnerReviews/OwnerReview"));
const ManagerReview = lazy(
  () => import("./screens/OwnerReviews/PMReviews/ManagerReview")
);
const ChatbotAnalytics = lazy(() => import("./screens/ChatbotAnalytics"));
const ProspectCommunication = lazy(
  () => import("./screens/ProspectCommunication")
);
const ServiceCatalogIndex = lazy(
  () => import("./screens/Service Catalog/Index")
);
const AccountCreationForm = lazy(
  () => import("./screens/Service Catalog/AccountCreation")
);
const ManageOrganization = lazy(
  () => import("./screens/Service Catalog/ManageOrganization")
);
const OwnerReport = lazy(
  () => import("./screens/PropertyOwnerReport/OwnerReport")
);
const GLCodeManager = lazy(() => import("./components/GL Code/GLCodeManager"));
const WorkOrderSummary = lazy(
  () => import("./screens/WorkOrderSummary/WorkOrderSummary")
);

const App = () => {
  const dev = process.env.DEV_MODE;
  const queryClient = new QueryClient();

  React.useEffect(() => {
    initGA();
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <Provider store={store}>
        <PersistGate loading={null} persistor={persistor}>
          <NextUIProvider>
            <Router>
              <AnalyticsTracker />
              {/* ScrollToTop component is placed here */}
              <ScrollToTop />
              <Toaster position="top-center" />
              <GlobalLiveAgentNotifier />
              <Suspense
                fallback={
                  <div
                    role="status"
                    className="flex min-h-[80vh] items-center justify-center text-sm text-gray-500"
                  >
                    Loading page...
                  </div>
                }
              >
                <Routes>
                  {/* <Route
                  path="/p/:slug"
                  element={
                    <PrivateRoute>
                      <CompareProperty />
                    </PrivateRoute>
                  }
                /> */}
                  <Route
                    path="/p/:slug"
                    element={
                      <PrivateRoute pageKey="units_market_research">
                        <UnitsSearch />
                      </PrivateRoute>
                    }
                  />
                  {/* <Route path="/" element={<Navigate to="/p/allunits" />} /> */}
                  <Route
                    path="/rental-market-research"
                    element={
                      <PrivateRoute pageKey="rental_market_research">
                        <IndependentMarketResearchPage />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/property/:slug"
                    element={
                      <PrivateRoute>
                        {" "}
                        <DrildownAppcopilot />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/property-report/:id"
                    element={
                      <PrivateRoute>
                        {" "}
                        <PropertyReport />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/portfolio"
                    element={
                      <PrivateRoute pageKey="portfolio_search">
                        {" "}
                        <PropertyunitsV2 />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/analytics-overview"
                    element={
                      <PrivateRoute pageKey="high_low_visualization">
                        {" "}
                        <AnalyticsOverview />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/report-score"
                    element={
                      <PrivateRoute pageKey="trends">
                        {" "}
                        <ScoreCardMOM />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/property-view/:id"
                    element={
                      <PrivateRoute>
                        {" "}
                        <DrilldownPropertyView />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/portfolio-errors/"
                    element={
                      <PrivateRoute>
                        {" "}
                        <PortfolioLogs />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/manage-units"
                    element={
                      <PrivateRoute>
                        {" "}
                        <Index />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/portfolio-dashboard"
                    element={
                      <PrivateRoute pageKey="portfolio_dashboard">
                        {" "}
                        <PortfolioDashboard />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/profit-and-loss"
                    element={
                      <PrivateRoute pageKey="profit_and_loss_statement">
                        {" "}
                        <ProfitAndLoss />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/lease-up-analysis"
                    element={
                      <PrivateRoute pageKey="lease_renewal_analysis">
                        {" "}
                        <LeaseUpAnalysis />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/vacancy-analysis"
                    element={
                      <PrivateRoute pageKey="vacancy_analysis">
                        {" "}
                        <VacancyAnalysis />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/delinquecy-analysis"
                    element={
                      <PrivateRoute pageKey="delinquency_analysis">
                        {" "}
                        <DelinquencyAnalysis />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/expense-ratio"
                    element={
                      <PrivateRoute pageKey="property_operating_expense_ratio_analysis">
                        {" "}
                        <ExpenseRatio />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/balance-sheet"
                    element={
                      <PrivateRoute>
                        {" "}
                        <BalanceSheet />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/cash-flow"
                    element={
                      <PrivateRoute pageKey="cashflow_statement">
                        {" "}
                        <Cashflow />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/investor-report"
                    element={
                      <PrivateRoute pageKey="owner_report">
                        {" "}
                        <OwnerReport />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/work-order-labor-summary"
                    element={
                      <PrivateRoute pageKey="work_order_labor_summary">
                        {" "}
                        <WorkOrderSummary />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/owner-communication/:id"
                    element={<OwnerReview />}
                  />
                  <Route
                    path="/pm-communication"
                    element={
                      <PrivateRoute pageKey="owner_pricing_communication">
                        {" "}
                        <ManagerReview />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/chatbot-analytics"
                    element={
                      <PrivateRoute pageKey="chatbot_analytics">
                        <ChatbotAnalytics />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/prospect-communication"
                    element={
                      <PrivateRoute pageKey="prospect_communication">
                        <ProspectCommunication />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/import-properties"
                    element={
                      <PrivateRoute>
                        {" "}
                        <FileUpload />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/service-catalog"
                    element={
                      <PrivateRoute>
                        <ServiceCatalogIndex />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/service-catalog/create-account"
                    element={
                      <PrivateRoute>
                        <AccountCreationForm />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/service-catalog/organization/:orgId"
                    element={
                      <PrivateRoute>
                        <ManageOrganization />
                      </PrivateRoute>
                    }
                  />
                  {/* <Route path="/signup" element={<SignUpPage />} /> */}
                  <Route
                    path="/"
                    element={
                      <PublicRoute>
                        {" "}
                        <LoginPage />
                      </PublicRoute>
                    }
                  />
                  <Route
                    path="/superuser"
                    element={
                      <PublicRoute>
                        <LoginPage isSuperuserLogin={true} />
                      </PublicRoute>
                    }
                  />
                  <Route
                    path="/glcode"
                    element={
                      <PrivateRoute pageKey="glcode">
                        <GLCodeManager />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/forgot-password"
                    element={
                      <PublicRoute>
                        <ForgotPassword />
                      </PublicRoute>
                    }
                  />
                  <Route
                    path="/reset-password"
                    element={
                      <PublicRoute>
                        <ResetPassword />
                      </PublicRoute>
                    }
                  />
                  <Route
                    path="/expired"
                    element={
                      <PublicRoute>
                        <TokenVerify />
                      </PublicRoute>
                    }
                  />

                  <Route
                    path="/change-password"
                    element={
                      <PrivateRoute>
                        <ChangePasswordPage />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/property-unit-details/:slug"
                    element={<PropertyUnitDetails />}
                  />
                  <Route
                    path="/profile"
                    element={
                      <PrivateRoute>
                        <Profile />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/settings"
                    element={
                      <PrivateRoute pageKey="settings">
                        <Settings />
                      </PrivateRoute>
                    }
                  />
                  <Route path="/support" element={<Support />} />
                  <Route
                    path="/invite"
                    element={
                      <PrivateRoute pageKey="invite">
                        <Invite />
                      </PrivateRoute>
                    }
                  />
                  {/* <Route path="/verify_invite" element={<OnBoard />} /> */}

                  <Route path="/proposed-rent/rule" element={<RulesForm />} />
                  <Route path="/verify_invite" element={<InviteVerify />} />
                  <Route path="/onboard" element={<OnBoard />} />
                </Routes>
              </Suspense>
            </Router>
          </NextUIProvider>
        </PersistGate>
      </Provider>
      <ReactQueryDevtools initialIsOpen={dev} position="bottom" />
    </QueryClientProvider>
  );
};

export default App;
